import { Typography } from 'antd';
import { useCallback, useMemo } from 'react';

import type {
  RangeInterface,
  RangeType,
} from '@app/shared/components/Range/types';
import { ClampedIntegerInput } from '@app/shared/components/Range/ui/ClampedIntegerInput';

import '@app/shared/components/Range/styles/Range.desktop.scss';

const { Text } = Typography;

const normalizeRangeValue = (
  value: number | null | undefined
): number | null => {
  if (value === null || value === undefined) return null;
  if (Number.isNaN(value)) return null;
  return value;
};

const clamp = (value: number, min?: number, max?: number): number => {
  let result = value;

  if (min !== undefined) {
    result = Math.max(min, result);
  }

  if (max !== undefined) {
    result = Math.min(max, result);
  }

  return result;
};

export const Range: RangeType = ({
  label,
  onChange,
  values,
  fromTitle = 'от',
  toTitle = 'до',
  inputsClassName = '',
  labelClassName = '',
  disabled = false,
  min = Number.MIN_SAFE_INTEGER,
  max = Number.MAX_SAFE_INTEGER,
}) => {
  const fromValue = useMemo(
    () => normalizeRangeValue(values.from),
    [values.from]
  );

  const toValue = useMemo(() => normalizeRangeValue(values.to), [values.to]);

  const changeHandler = useCallback(
    (next: Partial<RangeInterface>) => {
      const nextFrom = next.from !== undefined ? next.from : fromValue;
      const nextTo = next.to !== undefined ? next.to : toValue;

      onChange({ from: nextFrom, to: nextTo });
    },
    [fromValue, toValue, onChange]
  );

  const handleFromBlur = useCallback(() => {
    if (fromValue === null) return;

    const maxAvailableFromValue = toValue ?? max;
    const nextFromValue = clamp(fromValue, min, maxAvailableFromValue);

    if (nextFromValue === fromValue) return;

    changeHandler({ from: nextFromValue });
  }, [fromValue, toValue, min, max, changeHandler]);

  const handleToBlur = useCallback(() => {
    if (toValue === null) return;

    const minAvailableToValue = fromValue ?? min;
    const nextToValue = clamp(toValue, minAvailableToValue, max);

    if (nextToValue === toValue) return;

    changeHandler({ to: nextToValue });
  }, [toValue, fromValue, min, max, changeHandler]);

  return (
    <div className="range_container">
      {!!label && (
        <Text className={`range_label ${labelClassName}`}>{label}</Text>
      )}

      <div className={`range_inputs ${inputsClassName}`}>
        <ClampedIntegerInput
          addonBefore={<span>{fromTitle}</span>}
          value={fromValue}
          onValueChange={(nextValue) => {
            changeHandler({ from: nextValue });
          }}
          onBlur={handleFromBlur}
          disabled={disabled}
          min={min}
          max={max}
          style={{ width: '100%' }}
        />

        <ClampedIntegerInput
          addonBefore={<span>{toTitle}</span>}
          value={toValue}
          onValueChange={(nextValue) => {
            changeHandler({ to: nextValue });
          }}
          onBlur={handleToBlur}
          disabled={disabled}
          min={min}
          max={max}
          style={{ width: '100%' }}
        />
      </div>
    </div>
  );
};
